import React, {PureComponent} from 'react';
import {connect} from 'react-redux';
import {bindActionCreators} from 'redux';

import {
  Wrapper,
  Title,
  Head,
  LinkButton,
  Content,
} from '../../styles/pages/pages';
import {
  Info,
  SectionHeader,
  OrderStatus,
  SectionBody,
  TheButton,
  ErrorInfo,
  ContactLink,
  ArrivingHeader,
  TrackingDetails,
  TrackingDetailRow,
  TrackingDetailLabel,
  LabelText,
  LabelContentText,
} from './TrackingPage.style';
import {setCurrentRoute} from '../../actions';
import {
  JJ_LOCAL_STORAGE,
  JJ_TEL,
  ORDER_TRACKING,
  ROUTES,
} from '../../constants/constants';
import {trackOrder, setRouteTracker} from '../../actions/order.action';
import Loading from '../../components/Loading/Loading';
import {ExtraWrapper} from '../../styles/components/wrapper';
import {setLoginRedirect} from '../../actions/loginRedirect.action';
import {getDataFromLocalStorage} from '../../helpers/localStorage.helper';
import {getOrderStatus} from '../../helpers/data.helper';
import TrackingMap from '../../components/TrackingMap/TrackingMap';
import {getDropsStatus, resolveDestCoords} from '../../helpers/map.helper';

class TrackingPage extends PureComponent {
  constructor(props) {
    super(props);
    const {match} = props;
    this.state = {
      axOrderId: match.params.orderId,
      orderStatus: null,
      estimatedDeliveryTime: null,
    };
  }

  componentDidUpdate(prevProps, prevState) {
    const {auth, trackOrder, order, loading, errors} = this.props;
    if (
      auth &&
      loading &&
      (!prevProps.auth || this.state.axOrderId !== prevState.axOrderId)
    ) {
      if (!order || (order && !order.tracking)) {
        trackOrder(this.state.axOrderId);
      }
    }
    if (
      (!errors || (errors && !errors.trackOrder)) &&
      order &&
      order.tracking &&
      order.tracking.estimatedDeliveryTime &&
      order !== prevProps.order
    ) {
      const newStatus = getOrderStatus(order.tracking.estimatedDeliveryTime);
      if (newStatus.status !== this.state.orderStatus) {
        this.setState({
          orderStatus: newStatus.status,
          estimatedDeliveryTime: newStatus.estimatedDeliveryTime,
        });
      }
    }
  }

  async componentDidMount() {
    const {
      auth,
      setCurrentRoute,
      trackOrder,
      toggleLoginModal,
      setLoginRedirect,
    } = this.props;
    const {axOrderId} = this.state;
    const storedAuthToken = await getDataFromLocalStorage(
      JJ_LOCAL_STORAGE.TOKEN
    );
    if (!auth && !storedAuthToken) {
      setLoginRedirect('/order/track/' + axOrderId);
      toggleLoginModal();
      return;
    }
    setCurrentRoute(ROUTES.TRACKING);
    this.callRouteTracker(true);
    if (auth && auth.enhanced) {
      trackOrder(axOrderId);
    }
  }

  callRouteTracker = enabled => {
    const {axOrderId} = this.state;
    this.props.setRouteTracker(axOrderId, enabled);
  };

  componentWillUnmount() {
    this.callRouteTracker(false);
  }

  goBack = () => this.props.history.push(`/${ROUTES.ORDERS}`);

  render() {
    const {auth, order, errors, loading, profile} = this.props;
    const {axOrderId, orderStatus, estimatedDeliveryTime} = this.state;
    const tracking = order && order.tracking;
    const renderShoppingLink = (
      <LinkButton to="/">Continue shopping</LinkButton>
    );
    const isLoadingTracking = loading && loading.trackOrder;
    const hasError = errors && errors.trackOrder;
    const renderError = hasError && !isLoadingTracking && (
      <ErrorInfo>
        Sorry, No tracking information available for this order
      </ErrorInfo>
    );
    const renderLoading = (!auth || isLoadingTracking) && !hasError && (
      <Loading isLight={false} />
    );

    const dropsStatus = getDropsStatus(tracking, orderStatus);
    const renderResult = !isLoadingTracking && !hasError && tracking && (
      <>
        <TrackingDetails>
          <TrackingDetailRow>
            <TrackingDetailLabel>Order:</TrackingDetailLabel>
            <LabelText>#{axOrderId}</LabelText>
          </TrackingDetailRow>
          {tracking.driverName && (
            <TrackingDetailRow>
              <TrackingDetailLabel>Driver:</TrackingDetailLabel>
              <LabelText>
                {tracking.driverName}
                {tracking.driverRating !== null && (
                  <LabelContentText>
                    ({tracking.driverRating}/5⭐)
                  </LabelContentText>
                )}
              </LabelText>
            </TrackingDetailRow>
          )}
          {tracking.vehicleReg && (
            <TrackingDetailRow>
              <TrackingDetailLabel>Vehicle:</TrackingDetailLabel>
              <LabelText>{tracking.vehicleReg}</LabelText>
            </TrackingDetailRow>
          )}
          {dropsStatus && orderStatus !== ORDER_TRACKING.DELIVERED && (
            <TrackingDetailRow>
              <TrackingDetailLabel>Status:</TrackingDetailLabel>
              <LabelText>
                {orderStatus === ORDER_TRACKING.DELAYED
                  ? ORDER_TRACKING.ON_THE_WAY
                  : dropsStatus}
              </LabelText>
            </TrackingDetailRow>
          )}
        </TrackingDetails>
      </>
    );
    const deliveringFooterMsg =
      orderStatus === ORDER_TRACKING.DELIVERING &&
      `We take special care to endeavour that deliveries are made within your chosen delivery slot, however, please be advised that the actual delivery times may differ depending on the road conditions and traffic.`;
    const deliveryDelayFooterMsg =
      orderStatus === ORDER_TRACKING.DELAYED &&
      `We take special care to endeavour that deliveries are made within your chosen delivery slot, however, please be advised that the actual delivery times may differ depending on the road conditions and traffic.`;
    const deliveryCancelFooterMsg =
      orderStatus === ORDER_TRACKING.CANCELLED &&
      `Unfortunately, a problem occurred during delivery and we had to cancel your order. Payment will not be taken for this order.`;
    const renderCancelledContactDetails = orderStatus ===
      ORDER_TRACKING.CANCELLED && (
      <p>
        Please contact the customer service for more information on
        <ContactLink href={`tel: ${JJ_TEL}`}>01992 701 701</ContactLink>
        <p>We´re very sorry for the inconvenience.</p>
      </p>
    );
    const isDeliverying =
      orderStatus !== ORDER_TRACKING.DELIVERED &&
      orderStatus !== ORDER_TRACKING.CANCELLED;
    return (
      <Wrapper>
        <Head>
          <Title>Order status</Title>
          <TheButton onClick={this.goBack}>Back to My Orders</TheButton>
        </Head>
        <Content>
          <SectionHeader>
            {tracking && isDeliverying ? (
              <ArrivingHeader>
                Arriving today between{' '}
                <LabelText>
                  {orderStatus === ORDER_TRACKING.DELAYED
                    ? tracking.originalDeliverySlot
                    : tracking.estimatedDeliveryTime || estimatedDeliveryTime}
                </LabelText>
              </ArrivingHeader>
            ) : (
              tracking && (
                <ArrivingHeader>
                  Your Order <LabelText>#{axOrderId}</LabelText> is{' '}
                  {orderStatus === ORDER_TRACKING.DELIVERED && 'delivered'}
                  {orderStatus === ORDER_TRACKING.DELAYED && 'delayed'}
                  {orderStatus === ORDER_TRACKING.CANCELLED && 'cancelled'}
                </ArrivingHeader>
              )
            )}
            <OrderStatus>
              {orderStatus === ORDER_TRACKING.DELAYED
                ? ORDER_TRACKING.DELIVERING
                : orderStatus}
            </OrderStatus>
          </SectionHeader>
          <SectionBody>
            {renderLoading}
            {renderResult}
            {renderError}
          </SectionBody>
          {tracking &&
            isDeliverying &&
            tracking.destination &&
            (() => {
              const destCoords = resolveDestCoords(
                tracking.destination,
                profile
              );
              if (!destCoords) return null;
              const {lat: effectiveDestLat, lng: effectiveDestLng} = destCoords;

              // Resolve driver position: use currentLocation if valid, else fall
              // back to profile address so the truck starts somewhere sensible.
              let effectiveDriverLat = null;
              let effectiveDriverLng = null;
              if (
                orderStatus !== ORDER_TRACKING.DELAYED &&
                tracking.currentLocation
              ) {
                const rawLat = tracking.currentLocation.latitude;
                const rawLng = tracking.currentLocation.longitude;
                if (rawLat && rawLng) {
                  effectiveDriverLat = rawLat;
                  effectiveDriverLng = rawLng;
                }
              }
              if (!effectiveDriverLat || !effectiveDriverLng) {
                const profileLat =
                  profile && profile.address && profile.address.latitude;
                const profileLng =
                  profile && profile.address && profile.address.longitude;
                if (profileLat && profileLng) {
                  effectiveDriverLat = profileLat;
                  effectiveDriverLng = profileLng;
                }
              }
              return (
                <TrackingMap
                  driverLat={effectiveDriverLat}
                  driverLng={effectiveDriverLng}
                  driverLabel={tracking.driverName}
                  destLat={effectiveDestLat}
                  destLng={effectiveDestLng}
                  destLabel="Delivery address"
                  dropsBeforeYou={tracking.dropsBeforeYou}
                />
              );
            })()}
          <Info>
            {deliveringFooterMsg}
            {deliveryDelayFooterMsg}
            {deliveryCancelFooterMsg}
            {renderCancelledContactDetails}
          </Info>
        </Content>
        <ExtraWrapper>{renderShoppingLink}</ExtraWrapper>
      </Wrapper>
    );
  }
}

const mapStateToProps = state => ({
  auth: state.auth,
  order: state.order,
  errors: state.errors,
  loading: state.loading,
  profile: state.profile,
});

const mapDispatchToProps = dispatch => ({
  setCurrentRoute: bindActionCreators(setCurrentRoute, dispatch),
  trackOrder: bindActionCreators(trackOrder, dispatch),
  setLoginRedirect: bindActionCreators(setLoginRedirect, dispatch),
  setRouteTracker: bindActionCreators(setRouteTracker, dispatch),
});

export default connect(mapStateToProps, mapDispatchToProps)(TrackingPage);
